import React from 'react';
import { TrendingUp, Shield, Zap, Globe, DollarSign, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const CryptoEducation = () => {
  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <h1 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
            Understanding Crypto Launchpads
          </h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Learn how decentralized launchpads are revolutionizing early-stage crypto investments and shaping the future of blockchain fundraising.
          </p>
        </div>

        {/* What is a Launchpad */}
        <section className="mb-20">
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-12">
              <h2 className="display-medium mb-6" style={{ color: 'var(--text-primary)' }}>
                What is a Crypto Launchpad?
              </h2>
              <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                A crypto launchpad is a platform that helps blockchain projects raise capital by offering early access to their tokens before they hit major exchanges. Think of it as a "Kickstarter for crypto" - but with more liquidity, transparency, and investor protection through smart contracts.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                MatrixLaunch connects vetted blockchain startups with a global community of investors, providing a secure environment for token launches through decentralized technology and rigorous due diligence.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* How MatrixLaunch Earns */}
        <section className="mb-20">
          <h2 className="display-medium text-center mb-12" style={{ color: 'var(--text-primary)' }}>
            Platform Revenue Model
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <DollarSign className="text-brand-primary" size={32} />
                </div>
                <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>Platform Fees (2%)</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  We charge a minimal 2% fee on successful token raises. This fee covers platform operations, security audits, and infrastructure costs while keeping investments affordable.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Target className="text-brand-primary" size={32} />
                </div>
                <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>Project Listing Fees</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Projects pay listing fees for vetting, marketing, and featured placement. This ensures only serious, well-funded projects reach our investor community.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <TrendingUp className="text-brand-primary" size={32} />
                </div>
                <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>Token Holdings</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Strategic token allocations from successful projects create long-term value alignment between MatrixLaunch, projects, and investors.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Future of Launchpads */}
        <section className="mb-20">
          <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl">
            <CardContent className="p-12">
              <h2 className="display-medium mb-8" style={{ color: 'var(--text-primary)' }}>
                The Future of Crypto Launchpads
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>Market Growth Trajectory</h3>
                  <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                    The global crypto launchpad market is projected to reach <strong>$15 billion by 2027</strong>, driven by:
                  </p>
                  <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
                    <li>✓ Increasing institutional adoption of blockchain technology</li>
                    <li>✓ Growing demand for early-stage crypto investments</li>
                    <li>✓ Regulatory clarity in major markets (US, EU, Asia)</li>
                    <li>✓ Maturation of DeFi and Web3 ecosystems</li>
                  </ul>
                </div>
                <div>
                  <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>Innovation Trends</h3>
                  <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
                    <li>✓ <strong>Multi-chain launches:</strong> Projects launching simultaneously on Ethereum, BSC, Polygon</li>
                    <li>✓ <strong>AI-powered vetting:</strong> Machine learning for project risk assessment</li>
                    <li>✓ <strong>Social investing:</strong> Copy trading and community-driven allocation</li>
                    <li>✓ <strong>Dynamic pricing:</strong> Fair launch mechanisms replacing fixed price sales</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Stability & Risk Management */}
        <section className="mb-20">
          <h2 className="display-medium text-center mb-12" style={{ color: 'var(--text-primary)' }}>
            Platform Stability & Risk Management
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 flex-shrink-0 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                    <Shield className="text-brand-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Project Vetting Process</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Only 5-10% of applicants pass our rigorous screening:
                    </p>
                  </div>
                </div>
                <ul className="space-y-2 ml-16" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Team background verification (KYC)</li>
                  <li>• Smart contract security audits</li>
                  <li>• Tokenomics sustainability analysis</li>
                  <li>• Business model viability check</li>
                  <li>• Legal compliance review</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 flex-shrink-0 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                    <Zap className="text-brand-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Investor Protection</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Built-in safeguards for your investments:
                    </p>
                  </div>
                </div>
                <ul className="space-y-2 ml-16" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Vesting schedules for team tokens</li>
                  <li>• Liquidity lock mechanisms</li>
                  <li>• Anti-whale limits (max allocation caps)</li>
                  <li>• Refund clauses for failed projects</li>
                  <li>• Transparent fund tracking on-chain</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Market Position */}
        <section className="mb-20">
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
            <CardContent className="p-12">
              <h2 className="display-medium mb-6" style={{ color: 'var(--text-primary)' }}>
                Why Crypto Trading via Launchpads?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h4 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Early Access Advantage</h4>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Get tokens at 30-70% below listing price. Historical data shows launchpad investors average 3-8x returns on successful projects within first 6 months.
                  </p>
                </div>
                <div>
                  <h4 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Curated Selection</h4>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Skip thousands of scam projects. Our due diligence team filters out 90%+ of applications, presenting only high-quality opportunities.
                  </p>
                </div>
                <div>
                  <h4 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>Community Benefits</h4>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Join project communities early, influence roadmaps, and build network effects through our referral program earning passive income.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Future Outlook */}
        <section>
          <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl">
            <CardContent className="p-12">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 flex-shrink-0 bg-brand-primary rounded-sm flex items-center justify-center">
                  <Globe className="text-black" size={40} />
                </div>
                <div>
                  <h2 className="display-medium mb-4" style={{ color: 'var(--text-primary)' }}>
                    MatrixLaunch: Built for the Long Term
                  </h2>
                  <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                    Our diversified revenue model, strict risk management, and community-first approach ensure platform sustainability through market cycles. With backing from leading blockchain VCs and a proven track record of successful launches, MatrixLaunch is positioned as a cornerstone of the Web3 fundraising ecosystem.
                  </p>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <div className="text-3xl font-bold text-brand-primary mb-1">$47.5M+</div>
                      <div style={{ color: 'var(--text-muted)' }}>Total Raised</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-brand-primary mb-1">89</div>
                      <div style={{ color: 'var(--text-muted)' }}>Projects Launched</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-brand-primary mb-1">128K+</div>
                      <div style={{ color: 'var(--text-muted)' }}>Active Investors</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default CryptoEducation;
